// Direction C · Soft Workspace tokens.
// Source of truth: design-bundle/output/design_handoff_skedd_redesign/components/tokens.jsx

export type Accent = "indigo" | "amber" | "green" | "rose";

export type Density = "compact" | "regular" | "comfortable";

export type StatusSlot =
  | "draft"
  | "submitted"
  | "rework"
  | "waiting"
  | "approved"
  | "absence"
  | "rejected"
  | "rolled";

export type Scheme = "light" | "dark";

export interface SlotColors {
  bg: string;
  fg: string;
  border: string;
}

export type StatusTokens = Record<Scheme, Record<StatusSlot, SlotColors>>;

export interface Neutrals {
  bg: string;
  surface: string;
  surfaceAlt: string;
  border: string;
  borderStrong: string;
  text: string;
  textMuted: string;
  textDim: string;
  /** Подсветка колонки «сегодня» в сетке таймшита. В тёмной теме НЕ должна быть
   *  светлой (раньше использовался brand-0 ≈ белый → на нём не видно ни часов,
   *  ни иконки комментария). Тон — брендовый, но приглушённый под фон темы. */
  todayBg: string;
}

export interface AccentShades {
  50: string;
  100: string;
  500: string;
  600: string;
  fg: string;
}

export interface DensityPreset {
  rowHeight: number;
  cellPadding: number;
  containerPadding: number;
  gap: number;
  fontSize: number;
  h1Size: number;
  h2Size: number;
  buttonHeight: number;
}

export const NEUTRALS: Record<Scheme, Neutrals> = {
  light: {
    bg: "#f7f4ec",
    surface: "#fffdf7",
    surfaceAlt: "#f0ebdc",
    border: "#e3deca",
    borderStrong: "#d0caaf",
    text: "#23201a",
    textMuted: "#665f4f",
    textDim: "#9c9685",
    todayBg: "#eef1fb",
  },
  dark: {
    // Ревью тёмной темы (Claude Design, 21.07): держать шаг светлоты
    // bg → surface → surfaceAlt ≈ +6–8 ед., иначе поверхности сливаются с фоном.
    bg: "#171512",
    surface: "#26231d",
    surfaceAlt: "#302c25",
    border: "#3a352d",
    borderStrong: "#4a4438",
    text: "#f5f1e8",
    textMuted: "#b8b2a1",
    textDim: "#7a7365",
    // Приглушённый брендовый (индиго) тон на тёмном фоне: колонка «сегодня»
    // читается как выделенная, но текст и иконки на ней остаются видимыми.
    todayBg: "#26283a",
  },
};

export const ACCENTS: Record<Accent, AccentShades> = {
  indigo: { 50: "#f4f6fd", 100: "#e9ecfa", 500: "#5b6be0", 600: "#4a59d4", fg: "#ffffff" },
  amber: { 50: "#fbf6ea", 100: "#f7ebd2", 500: "#c4831f", 600: "#a86f15", fg: "#ffffff" },
  green: { 50: "#eef5ee", 100: "#dcecdd", 500: "#3d8b40", 600: "#2f6e32", fg: "#ffffff" },
  rose: { 50: "#fbedf1", 100: "#f7dde4", 500: "#c54a64", 600: "#a83a52", fg: "#ffffff" },
};

export const STATUS: StatusTokens = {
  light: {
    draft: { bg: "#f4f1ea", fg: "#6a6555", border: "#e3deca" },
    submitted: { bg: "#e8eef9", fg: "#3a5598", border: "#d2dcf0" },
    rework: { bg: "#fbe8d2", fg: "#9a5510", border: "#f1d4b0" },
    waiting: { bg: "#fbf0d2", fg: "#9a7510", border: "#f1e2b0" },
    approved: { bg: "#dceddd", fg: "#2f6e32", border: "#c4debd" },
    absence: { bg: "#ede0f0", fg: "#6a3a98", border: "#dcc8e2" },
    rejected: { bg: "#fadcdc", fg: "#9a3838", border: "#f1c2c2" },
    rolled: { bg: "#f4f1ea", fg: "#8a8578", border: "#e3deca" },
  },
  dark: {
    // Ревью 21.07: на тёмном «дневные» fg звенят — убавили хрому,
    // красный сделали мягче/розовее, всегда с фоновой плашкой (bg).
    draft: { bg: "#2a2620", fg: "#bcb6a3", border: "#3a352a" },
    submitted: { bg: "#202b3a", fg: "#a8c8e8", border: "#2c3a4d" },
    rework: { bg: "#33291f", fg: "#e6bd82", border: "#4a3825" },
    waiting: { bg: "#33291f", fg: "#e6bd82", border: "#4a3825" },
    approved: { bg: "#1f3329", fg: "#82c29a", border: "#2a4536" },
    absence: { bg: "#2d2438", fg: "#c0a8e0", border: "#3d3148" },
    rejected: { bg: "#3a2420", fg: "#e08a8a", border: "#4d3028" },
    rolled: { bg: "#2a2620", fg: "#9c9685", border: "#3a352a" },
  },
};

export const DENSITIES: Record<Density, DensityPreset> = {
  compact: {
    rowHeight: 30,
    cellPadding: 6,
    containerPadding: 10,
    gap: 8,
    fontSize: 12.5,
    h1Size: 22,
    h2Size: 17,
    buttonHeight: 28,
  },
  regular: {
    rowHeight: 38,
    cellPadding: 9,
    containerPadding: 14,
    gap: 12,
    fontSize: 13,
    h1Size: 26,
    h2Size: 19,
    buttonHeight: 32,
  },
  comfortable: {
    rowHeight: 46,
    cellPadding: 12,
    containerPadding: 18,
    gap: 16,
    fontSize: 13.5,
    h1Size: 30,
    h2Size: 21,
    buttonHeight: 36,
  },
};

export const SHADOWS = {
  light: {
    sm: "0 1px 2px rgba(20,16,8,0.04)",
    md: "0 4px 12px rgba(20,16,8,0.06)",
    lg: "0 12px 32px rgba(20,16,8,0.08)",
  },
  dark: {
    sm: "0 1px 2px rgba(0,0,0,0.4)",
    md: "0 4px 12px rgba(0,0,0,0.4)",
    lg: "0 12px 32px rgba(0,0,0,0.5)",
  },
};

export const FONTS = {
  body: '"Inter", "Helvetica Neue", system-ui, sans-serif',
  heading: '"Inter", system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, monospace',
};

/**
 * Project palette v2 — 10 цветов из Direction C, выданных Claude Design.
 * Источник: design-bundle/output/.../components/palette-v2.jsx → PROJECT_PALETTE_V2.
 * Все 10 в одном «весе»: lightness ~0.50–0.58, chroma ~0.10–0.13.
 *
 * Бэк хранит цвет на каждом проекте (`Project.color`); этот массив здесь —
 * как референс для UI-цветового пикера, фоллбеков и тестов. Дублировать его
 * в migration intentional: миграции должны быть self-contained, а tokens
 * нельзя импортировать из миграций без приключений.
 */
export const PROJECT_PALETTE: { hex: string; name: string }[] = [
  { hex: "#5b6be0", name: "indigo" },
  { hex: "#3d8b40", name: "green" },
  { hex: "#c4831f", name: "amber" },
  { hex: "#8a6fb8", name: "violet" },
  { hex: "#2f8a8c", name: "teal" },
  { hex: "#4a8fc4", name: "sky" },
  { hex: "#c45a64", name: "rose" },
  { hex: "#d27250", name: "coral" },
  { hex: "#7a8a3d", name: "olive" },
  { hex: "#9c6a4f", name: "clay" },
];
